//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import BuildsIcon from '../../../icons/Build.png';
import { UnifiedCardData, formatDate, displayOrNA } from './UnifiedCardData';
import RelatedLinks from './RelatedLinks';

export const BuildCard: React.FC<{ card: UnifiedCardData }> = ({ card }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="mb-6 rounded-lg bg-[#f2f4f5] p-6 dark:bg-[#393939]"
      id="id-build-card-container"
      data-testid="build-card-container"
    >
      <div
        className="mb-4 flex items-start gap-3"
        id="id-build-card-content"
        data-testid="build-card-content"
      >
        <div
          className="mt-0.5 shrink-0"
          id="id-build-card-icon-wrapper"
          data-testid="build-card-icon-wrapper"
        >
          <div
            className="flex size-8 items-center justify-center rounded"
            id="id-build-card-icon"
            data-testid="build-card-icon"
          >
            <img
              src={BuildsIcon}
              alt="Build icon"
              className="size-8 object-contain"
            />
          </div>
        </div>
        <div
          className="min-w-0 flex-1"
          id="id-build-card-details"
          data-testid="build-card-details"
        >
          <div
            className="mb-1 flex items-center gap-2"
            id="id-build-card-title-row"
            data-testid="build-card-title-row"
          >
            <span
              className="text-[12px] text-gray-500 dark:text-gray-400"
              id="id-build-card-type-label"
              data-testid="build-card-type-label"
            >
              {t('build')}
            </span>
            <span
              className="text-[12px] text-gray-300 dark:text-gray-600"
              id="id-build-card-title-separator"
              data-testid="build-card-title-separator"
            >
              |
            </span>
            <span
              className="cursor-default text-[14px] font-medium text-[#0043ce] dark:text-brand-interactiveDark"
              id="id-build-card-title"
              data-testid="build-card-title"
            >
              {card.cardTitle}
            </span>
          </div>
          <p className="mt-1 text-[12px] text-gray-600 dark:text-gray-300">
            <span
              id="id-build-card-created-label"
              data-testid="build-card-created-label"
            >
              {t('created')}:
            </span>{' '}
            <span
              id="id-build-card-created-value"
              data-testid="build-card-created-value"
            >
              {(() => {
                const formatted = formatDate(card.created);
                return formatted === '--' ? t('not-available') : formatted;
              })()}
            </span>
            <span
              className="mx-1"
              id="id-build-card-separator-1"
              data-testid="build-card-separator-1"
            >
              |
            </span>
            <span
              id="id-build-card-requestor-label"
              data-testid="build-card-requestor-label"
            >
              {t('requestor')}:
            </span>
            <span
              className="ml-1"
              id="id-build-card-requestor-value"
              data-testid="build-card-requestor-value"
            >
              {displayOrNA(card.requestor || card.owner, t)}
            </span>
            <span
              className="mx-1"
              id="id-build-card-separator-2"
              data-testid="build-card-separator-2"
            >
              |
            </span>
            <span
              id="id-build-card-loop-label"
              data-testid="build-card-loop-label"
            >
              {t('loop')}:
            </span>
            <span
              className="ml-1"
              id="id-build-card-loop-value"
              data-testid="build-card-loop-value"
            >
              {displayOrNA(card.loop || card.loopName, t)}
            </span>
            <span
              className="mx-1"
              id="id-build-card-separator-3"
              data-testid="build-card-separator-3"
            >
              |
            </span>
            <span
              id="id-build-card-application-label"
              data-testid="build-card-application-label"
            >
              {t('application')}:
            </span>
            <span
              className="ml-1"
              id="id-build-card-application-value"
              data-testid="build-card-application-value"
            >
              {displayOrNA(card.application, t)}
            </span>
            <span
              className="mx-1"
              id="id-build-card-separator-4"
              data-testid="build-card-separator-4"
            >
              |
            </span>
            <span
              id="id-build-card-commits-label"
              data-testid="build-card-commits-label"
            >
              {t('commits')}:
            </span>
            <span
              className="ml-1"
              id="id-build-card-commits-value"
              data-testid="build-card-commits-value"
            >
              {displayOrNA(card.commits, t)}
            </span>
          </p>
          <p className="mt-1 text-[12px] text-gray-600 dark:text-gray-300">
            <span
              id="id-build-card-source-label"
              data-testid="build-card-source-label"
            >
              {t('source')}:
            </span>
            <span
              className="ml-1"
              id="id-build-card-source-value"
              data-testid="build-card-source-value"
            >
              {displayOrNA(card.buildSource, t)}
            </span>
            <span
              className="mx-1"
              id="id-build-card-separator-5"
              data-testid="build-card-separator-5"
            >
              |
            </span>
            <span
              id="id-build-card-branch-label"
              data-testid="build-card-branch-label"
            >
              {t('branch')}:
            </span>
            <span
              className="ml-1"
              id="id-build-card-branch-value"
              data-testid="build-card-branch-value"
            >
              {displayOrNA(card.buildBranch, t)}
            </span>
            <span
              className="mx-1"
              id="id-build-card-separator-6"
              data-testid="build-card-separator-6"
            >
              |
            </span>
            <span
              id="id-build-card-labels-label"
              data-testid="build-card-labels-label"
            >
              {t('labels')}:
            </span>
            <span
              className="ml-1"
              id="id-build-card-labels-value"
              data-testid="build-card-labels-value"
            >
              {displayOrNA(card.buildLabel, t)}
            </span>
          </p>

          {/* Links Related - Accordion */}
          <div
            className="mt-4 border-t border-gray-300 pt-4 dark:border-gray-600"
            id="id-build-card-links-section"
            data-testid="build-card-links-section"
          >
            <button
              onClick={() => setExpanded((prev) => !prev)}
              className="flex items-center gap-2 text-[12px] text-[#0043ce] transition-colors hover:underline dark:text-brand-interactiveDark"
            >
              <span
                id="id-build-card-links-label"
                data-testid="build-card-links-label"
              >
                {t('links-related-result')}
              </span>
              <ChevronDown
                size={16}
                className={`transition-transform ${expanded ? 'rotate-180' : ''}`}
              />
            </button>
            {expanded && <RelatedLinks card={card} primaryLabel="build-url" />}
          </div>
        </div>
      </div>
    </div>
  );
};
